import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import './CustomAlert.css';

const CustomAlert = ({ 
    show, 
    onClose, 
    type = 'info', 
    title, 
    message, 
    duration = 0 
}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (show) {
            setIsVisible(true);
            if (duration > 0) {
                const timer = setTimeout(() => {
                    handleClose();
                }, duration);
                return () => clearTimeout(timer);
            }
        } else {
            setIsVisible(false);
        }
    }, [show, duration]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            if (onClose) onClose();
        }, 300); // Wait for animation to complete
    };

    const getAlertConfig = () => {
        switch (type) {
            case 'success':
                return {
                    icon: faCheckCircle,
                    bgClass: 'bg-success',
                    textClass: 'text-white',
                    borderClass: 'border-success'
                };
            case 'error':
            case 'danger':
                return {
                    icon: faExclamationTriangle,
                    bgClass: 'bg-danger',
                    textClass: 'text-white',
                    borderClass: 'border-danger'
                };
            case 'warning':
                return {
                    icon: faExclamationTriangle,
                    bgClass: 'bg-warning',
                    textClass: 'text-dark',
                    borderClass: 'border-warning'
                };
            case 'info':
            default:
                return {
                    icon: faInfoCircle,
                    bgClass: 'bg-info',
                    textClass: 'text-white',
                    borderClass: 'border-info'
                };
        }
    };

    const config = getAlertConfig();

    if (!show && !isVisible) return null;

    return (
        <div className={`custom-alert-overlay ${isVisible ? 'show' : ''}`}>
            <div className={`custom-alert ${config.bgClass} ${config.textClass} ${config.borderClass}`}>
                <div className="d-flex align-items-start">
                    <div className="me-3">
                        <FontAwesomeIcon icon={config.icon} size="lg" />
                    </div>
                    <div className="flex-grow-1">
                        {title && (
                            <h6 className="mb-2 fw-bold">{title}</h6>
                        )}
                        <p className="mb-0">{message}</p>
                    </div>
                    <button 
                        className="btn btn-sm btn-link p-0 ms-2"
                        onClick={handleClose}
                        style={{ color: 'inherit' }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Custom Confirm Dialog Component
const CustomConfirm = ({ 
    show, 
    onClose, 
    onConfirm,
    title = 'Confirm Action', 
    message = 'Are you sure?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'warning'
}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (show) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [show]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            if (onClose) onClose();
        }, 300);
    };

    const handleConfirm = () => {
        setIsVisible(false);
        setTimeout(() => {
            if (onConfirm) onConfirm();
        }, 300);
    };

    const getAlertConfig = () => {
        switch (type) {
            case 'danger':
                return {
                    icon: faExclamationTriangle,
                    bgClass: 'bg-danger',
                    textClass: 'text-white',
                    borderClass: 'border-danger',
                    confirmBtnClass: 'btn-danger'
                };
            case 'warning':
                return {
                    icon: faExclamationTriangle,
                    bgClass: 'bg-warning',
                    textClass: 'text-dark',
                    borderClass: 'border-warning',
                    confirmBtnClass: 'btn-warning'
                };
            case 'info':
            default:
                return {
                    icon: faInfoCircle,
                    bgClass: 'bg-info',
                    textClass: 'text-white',
                    borderClass: 'border-info',
                    confirmBtnClass: 'btn-info'
                };
        }
    };

    const config = getAlertConfig();

    if (!show && !isVisible) return null;

    return (
        <div className={`custom-alert-overlay ${isVisible ? 'show' : ''}`}>
            <div className={`custom-alert ${config.bgClass} ${config.textClass} ${config.borderClass}`}>
                <div className="d-flex align-items-start">
                    <div className="me-3">
                        <FontAwesomeIcon icon={config.icon} size="lg" />
                    </div>
                    <div className="flex-grow-1">
                        <h6 className="mb-2 fw-bold">{title}</h6>
                        <p className="mb-3">{message}</p>
                        <div className="d-flex gap-2 justify-content-end">
                            <button 
                                className="btn btn-secondary btn-sm"
                                onClick={handleClose}
                            >
                                {cancelText}
                            </button>
                            <button 
                                className={`btn ${config.confirmBtnClass} btn-sm`}
                                onClick={handleConfirm}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Custom Alert Hook for easy usage
export const useCustomAlert = () => {
    const [alertState, setAlertState] = useState({
        show: false,
        type: 'info',
        title: '',
        message: '',
        duration: 0
    });

    const [confirmState, setConfirmState] = useState({
        show: false,
        title: 'Confirm Action',
        message: 'Are you sure?',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: null
    });

    const showAlert = (type, title, message, duration = 0) => {
        setAlertState({
            show: true,
            type,
            title,
            message,
            duration
        });
    };

    const hideAlert = () => {
        setAlertState(prev => ({ ...prev, show: false }));
    };

    const showConfirm = (title, message, onConfirm, options = {}) => {
        setConfirmState({
            show: true,
            title,
            message,
            confirmText: options.confirmText || 'Confirm',
            cancelText: options.cancelText || 'Cancel',
            type: options.type || 'warning',
            onConfirm
        });
    };

    const hideConfirm = () => {
        setConfirmState(prev => ({ ...prev, show: false }));
    };

    const handleConfirm = () => {
        if (confirmState.onConfirm) {
            confirmState.onConfirm();
        }
        hideConfirm();
    };

    const AlertComponent = () => (
        <>
            <CustomAlert
                show={alertState.show}
                onClose={hideAlert}
                type={alertState.type}
                title={alertState.title}
                message={alertState.message}
                duration={alertState.duration}
            />
            <CustomConfirm
                show={confirmState.show}
                onClose={hideConfirm}
                onConfirm={handleConfirm}
                title={confirmState.title}
                message={confirmState.message}
                confirmText={confirmState.confirmText}
                cancelText={confirmState.cancelText}
                type={confirmState.type}
            />
        </>
    );

    return {
        showAlert,
        hideAlert,
        showConfirm,
        hideConfirm,
        AlertComponent
    };
};

export default CustomAlert;
